/**
 * Access levels: what Sky may see, say and open for the person in front of
 * her.
 *
 * Four levels, in order. Operations sees the airport in operation; engineering
 * adds the engines beneath it; executive adds the chief executive's desks;
 * master sees everything. A screen belongs to a level, a question can name a
 * topic that belongs to a level, and Sky answers only up to the level of the
 * person asking. What the chief executive is deciding, planning or working on
 * is executive material: an operations user asking about it is told it is
 * outside their access, not what it is.
 *
 * The platform issues the level with the sign-in: it is a property of the
 * roles a person holds, resolved on the server and returned with the identity
 * (migration 0057). The setting on this copy can only ever LOWER it — somebody
 * may choose to work at operations for the afternoon, and nobody may give
 * themselves the executive desks from a Settings panel.
 *
 * Against a platform older than 0057, or the sample, there is no issued level
 * and the groups are read instead. That fallback says so wherever the level is
 * shown, because a guess presented as an issued level is the failure this
 * module exists to prevent.
 */
import { useSyncExternalStore } from "react";
import type { Principal } from "../api/dataSource";

export type AccessLevel = "operations" | "engineering" | "executive" | "master";

export const LEVELS: readonly AccessLevel[] = ["operations", "engineering", "executive", "master"];

export const LEVEL_LABEL: Record<AccessLevel, string> = {
  operations: "Operations",
  engineering: "Engineering",
  executive: "Executive",
  master: "Master",
};

export const LEVEL_WHAT: Record<AccessLevel, string> = {
  operations: "the airport in operation: the dashboard, work orders, inspections, alerts, the map, the library",
  engineering: "operations, plus the engines: reliability, drift, the programme, what-if, liability, the ledger, capacity, testing, projects",
  executive: "engineering, plus the chief executive's desks: the Decision Room, crisis, cash, expansion, capital, the regulator, benchmark, improvement, green",
  master: "everything, including the settings that grant access",
};

const rank = (l: AccessLevel) => LEVELS.indexOf(l);
export const atLeast = (have: AccessLevel, need: AccessLevel) => rank(have) >= rank(need);

/**
 * The level the platform issued, or a reading of the groups where it issued
 * none. `issued` is what the caller shows the person: a guess must not be
 * presented as a grant.
 */
export function grantedLevel(p: Principal | undefined): { level: AccessLevel; issued: boolean } {
  if (!p) return { level: "operations", issued: false };
  if (p.accessLevel) return { level: p.accessLevel, issued: true };
  /* no level from the platform: read the groups, and say that is what this is */
  if (p.permissions.includes("*") || p.method === "sample") return { level: "master", issued: false };
  const g = p.groups.map((x) => x.toLowerCase()).join(" ");
  /* executive first: "Ops Directors" matched \bops\b before this order was fixed,
     and a director was shown the operations app */
  if (/exec|ceo|chief|board|director/.test(g)) return { level: "executive", issued: false };
  if (/engineer|maintenance|supervisor|planner|reliab/.test(g)) return { level: "engineering", issued: false };
  if (/\bops\b|operations|dispatch|controller|technician|inspector/.test(g)) return { level: "operations", issued: false };
  return { level: "operations", issued: false };
}

/** Kept for callers that only want the level. */
export const derivedLevel = (p: Principal | undefined): AccessLevel => grantedLevel(p).level;

/**
 * The level in force.
 *
 * The setting on this copy may only lower what the platform granted. Raising
 * it is not refused with a message — it simply cannot happen here, because a
 * client that can widen its own access is not access control.
 */
export function levelOf(p: Principal | undefined, override: AccessLevel | undefined): AccessLevel {
  const granted = grantedLevel(p).level;
  if (!override) return granted;
  return atLeast(granted, override) ? override : granted;
}

/* ── which screen belongs to which level ──────────────────────────────── */

/* the settings that decide what other people may do */
const MASTER_ROUTES = ["/call-policy"];
const EXECUTIVE_ROUTES = [
  "/decision-assist",
  "/crisis",
  "/cash",
  "/capacity-expansion",
  "/capex",
  "/regulatory",
  "/benchmark",
  "/improvement",
  "/green-program",
  "/executive-kpis",
  "/financials",
  "/approvals",
  "/sustainability",
];
const ENGINEERING_ROUTES = [
  /* costs, book values and the capital plan */
  "/asset-lifecycle",
  /* pay and manpower cost; the platform also refuses them without hr:read */
  "/hr",
  "/hr-people",
  "/hr-attendance",
  "/hr-records",
  "/hr-notifications",
  "/hr-calculators",
  "/hr-reports",
  "/hr-access",
  "/hr-cost",
  "/hr-labour",
  "/hr-saudization",
  "/hr-rules",
  "/engineering",
  "/visual-analytics",
  "/maintenance",
  "/maintenance-reporting",
  "/projects",
  "/testing-records",
  "/testing",
  "/programme",
  "/what-if",
  "/reliability",
  "/drift",
  "/liability",
  "/ledger",
  "/capacity",
  "/capital-projects",
  "/planning",
  "/weather",
  /* Deep thinking as rooms: the same engineering-level engines */
  "/think",
  "/decide",
  "/window",
  "/trust",
];

export function screenLevel(route: string): AccessLevel {
  const path = route.split("?")[0];
  if (MASTER_ROUTES.some((r) => path === r || path.startsWith(`${r}/`))) return "master";
  if (EXECUTIVE_ROUTES.some((r) => path === r || path.startsWith(`${r}/`))) return "executive";
  if (ENGINEERING_ROUTES.some((r) => path === r || path.startsWith(`${r}/`))) return "engineering";
  return "operations";
}

export const canSee = (have: AccessLevel, route: string) => atLeast(have, screenLevel(route));

/* ── which question belongs to which level ────────────────────────────── */

const EXECUTIVE_TOPICS =
  /\b(ceo|chief executive|the board|board line|decision room|decision assist|decisions? (on|about|being|the ceo)|what (is|are) (the )?(ceo|executives?|management|leadership) (doing|deciding|planning|working)|capital envelope|envelope|cash|burn rate|bottom line|margin|revenue|benchmark|peers?|expansion|saturat|capex|capital project|crisis desk|playbook|drill|regulator|inspection date|undertaking|leed|green programme|green program|carbon|strategy|strategic|business improvement|initiative|payback|executive)\b/i;
const ENGINEERING_TOPICS =
  /\b(reliability|weibull|survival|drift|programme|what-if|counterfactual|liability|deferred cost|ledger|divergence|capacity model|closure cost|critical positions?|hold points?|stage gates?|test records?|friction survey|pci|retro-?reflectiv)\b/i;

/** The level a question needs by its topic, whatever screen it is asked on. */
export function topicLevel(q: string): AccessLevel {
  if (EXECUTIVE_TOPICS.test(q)) return "executive";
  if (ENGINEERING_TOPICS.test(q)) return "engineering";
  return "operations";
}

/** What Sky says when a question is above the person's level. Names the level, never the content. */
export function refusalFor(have: AccessLevel, need: AccessLevel): string {
  return `That is ${LEVEL_LABEL[need].toLowerCase()} material, and your access here is ${LEVEL_LABEL[have].toLowerCase()}. I do not read or repeat what is above your level — not what is on those screens, and not what anyone is deciding or working on there. I can help with ${LEVEL_WHAT[have]}.`;
}

/* ── the setting on this copy, readable outside the provider ──────────── */

const KEY = (who: string) => `skylogics.assistant.access.${who}`;
const listeners = new Set<() => void>();

export function readAccessOverride(who: string): AccessLevel | undefined {
  try {
    const v = localStorage.getItem(KEY(who));
    return v && (LEVELS as readonly string[]).includes(v) ? (v as AccessLevel) : undefined;
  } catch {
    return undefined;
  }
}

export function writeAccessOverride(who: string, level: AccessLevel | undefined): void {
  try {
    if (level) localStorage.setItem(KEY(who), level);
    else localStorage.removeItem(KEY(who));
  } catch {
    /* storage off */
  }
  for (const l of listeners) l();
}

/** The level in force for this principal, live — for the shell, the router and Sky alike. */
export function useAccessLevel(p: Principal | undefined, who: string): AccessLevel {
  const override = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => readAccessOverride(who) ?? "",
    () => "",
  );
  return levelOf(p, override ? (override as AccessLevel) : undefined);
}

/* ── where a question lives ───────────────────────────────────────────── */

const HOMES: { test: RegExp; route: string; title: string }[] = [
  { test: /\b(decision room|decisions?|deciding|ceo|chief executive|the board|one-way door|flip point|robust option)\b/i, route: "/decision-assist", title: "Decision Room" },
  { test: /\b(crisis|playbook|drill|emergency (plan|exercise)|readiness)\b/i, route: "/crisis", title: "Crisis Desk" },
  { test: /\b(urgent inspection|after (the )?(storm|rain|sandstorm|snow)|standing water|flood(ing|ed)?|sandstorm|rwycc|runway condition (code|report)|snowtam|ficon)\b/i, route: "/urgent-inspection", title: "Urgent Inspection" },
  { test: /\b(cash|burn( rate)?|bottom line|margin|revenue|income a day|on the table|cost to cut)\b/i, route: "/cash", title: "Cash & Bottom Line" },
  { test: /\b(expansion|expand|saturat|run out of (room|stands|capacity)|terminal capacity|new pier|apron expansion)\b/i, route: "/capacity-expansion", title: "Capacity & Expansion" },
  { test: /\b(capex|capital (projects?|portfolio|programme)|stage gates?|hold points?|rebaseline)\b/i, route: "/capex", title: "Capital Projects" },
  { test: /\b(regulator|regulatory|gaca|inspection date|inspector|obligations?|certificates?|undertakings?|limits? crossed)\b/i, route: "/regulatory", title: "Regulatory Demands" },
  { test: /\b(benchmark|peers?|compare (us|ourselves|with)|rank(ed|ing)? (against|among)|other airports)\b/i, route: "/benchmark", title: "Benchmark" },
  { test: /\b(improvement|efficien\w*|initiatives?|payback|leak(s|ing)? value)\b/i, route: "/improvement", title: "Business Improvement" },
  { test: /\b(carbon|green (programme|program)|leed|emissions?|net zero|aca level|accreditation|renewable)\b/i, route: "/green-program", title: "Green & LEED" },
  { test: /\b(reliability|weibull|survival|wear-?out)\b/i, route: "/reliability", title: "Reliability" },
  { test: /\b(drift)\b/i, route: "/drift", title: "Drift" },
  { test: /\b(what-?if|counterfactual)\b/i, route: "/what-if", title: "What-if" },
  { test: /\b(liability|deferred cost|cost of waiting)\b/i, route: "/liability", title: "Liability" },
  { test: /\b(ledger|divergence|records disagree)\b/i, route: "/ledger", title: "Ledger" },
  { test: /\b(capacity model|closure cost|critical positions?|stranded)\b/i, route: "/capacity", title: "Capacity" },
];

/** The screen a question belongs to, when it clearly belongs to one she knows. */
export function routeForTopic(q: string): { route: string; title: string } | null {
  for (const h of HOMES) if (h.test.test(q)) return { route: h.route, title: h.title };
  return null;
}
